import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { IsoDateSchema, MoneyStringSchema, PctStringSchema } from './primitives.js';
const c = initContract();
/**
 * PART 15.6 (ADM-01 General Settings) + PART 16.6 — the org-settings
 * vocabulary, the single source of truth for every company switch.
 *
 * The spec ships 33 settings in 9 groups, each with a SCOPE that decides who
 * may change it (§16.6, "a second, separate ladder"):
 *
 *   personal — the person's own, always changeable, never audited;
 *   role     — a default for everybody, changed by Owner/Administrator;
 *   org      — one value for the whole company, changed by Owner/Administrator;
 *   owner    — the Owner's alone, INVISIBLE to everybody else (BR-ADM-01-02).
 *
 * Beyond the 33, this registry carries the EXTENSION settings the spec's own
 * open questions promote onto this screen (marked `extension: true`):
 *   - OQ-16-05 ("Should the $25,000 purchase-order threshold, the ±10%
 *     variance band … become settings? Yes, all of them, on the General
 *     Settings screen"): the PO and payment approval limits and the count
 *     variance tolerance;
 *   - EC-19-73: the default VAT rate as one company setting;
 *   - SAL-01 §8a: the four quotation feature switches.
 * The group counts on screen therefore exceed the prototype sketch — a
 * deliberate, recorded deviation.
 *
 * VALUE ENCODING (JSON, stored in org_settings.value / user_settings.value):
 *   switch → boolean · number → integer · money/percent → decimal STRING
 *   (§2.1 — money never travels as a JS number) · date → 'YYYY-MM-DD' ·
 *   enum → one of the listed option strings.
 *
 * The registry is CLOSED: an unknown key is a 404 at the API and refused by
 * the settings service. Adding a setting = adding it here (and its EN/AR
 * label + hint in the web dictionaries — BR-ADM-01-16).
 */
export const SETTING_GROUPS = [
    'fiscal',
    'treasury',
    'checks',
    'invoicing',
    /**
     * Wave 1C (ERP gap analysis, Oct 2026) — the purchasing cycle's own rows:
     * the three-way-match tolerance and the default supplier term. A group of
     * its own so a purchasing manager finds them together rather than filed
     * under the sales group's "Invoices" heading.
     */
    'purchasing',
    'commissions',
    'inventory',
    /**
     * INV-08 §12's [NOT IN PROTOTYPE — decide at build time]: "The mapping from
     * reason to account does not exist in the prototype. Build it as a settings
     * table, one account per reason, so an accountant can change it without a
     * developer." A group of its own so ADM-01's rail shows the twelve reason
     * rows together, in the voucher screen's own §5.2 order.
     */
    'stock_accounts',
    'manufacturing',
    'data',
    'security',
    'interface',
    /**
     * INV-H6 / UB-03 (bug triage, Oct 2026) — what the Print button puts on
     * paper: header and footer text, which columns, which paper, which
     * language. A group of its own because the eleven rows are drawn together
     * on the Printing screen (`/settings/printing`) with a live preview, and a
     * reader of ADM-01's rail should find them under one name.
     */
    'print',
    /**
     * ADM-08's six. A group of its own rather than more `interface` rows,
     * because the two answer different questions and live on different screens
     * — see `personalization` on SettingDef below, and OQ-15-12, which asks for
     * exactly this decision to be recorded.
     */
    'appearance',
];
export const SETTING_SCOPES = ['personal', 'role', 'org', 'owner'];
function def(d) {
    return d;
}
/**
 * All settings, group-major in the spec's own §15.6.7 order. The 33 spec rows
 * carry their exact defaults from the tables; extensions carry the constants
 * they replace.
 */
export const SETTINGS_REGISTRY = {
    // ── Fiscal & Cycle — 3, all org ───────────────────────────────────────────
    'fiscal.cycle_start': def({ group: 'fiscal', scope: 'org', type: 'date', default: '2026-01-01' }),
    'fiscal.cycle_end': def({ group: 'fiscal', scope: 'org', type: 'date', default: '2026-12-31' }),
    /**
     * N-16 / EC-19-65 — the company clock. Every business DATE in the product
     * ("today" for a posting, an attendance day, a cheque's due date) is worked
     * out in this zone; see `company-clock.ts` for the IANA mapping and the
     * instant-vs-date line.
     */
    'fiscal.timezone': def({
        group: 'fiscal',
        scope: 'org',
        type: 'enum',
        options: ['arabia', 'cairo', 'utc'],
        default: 'arabia',
    }),
    // ── Treasury & Currency — 3 (+2 extensions) ───────────────────────────────
    'treasury.cash_box_credit': def({ group: 'treasury', scope: 'org', type: 'switch', default: false }),
    /** The number behind the sales credit check and the over-limit chip (MD-01). */
    'treasury.debt_limit': def({ group: 'treasury', scope: 'org', type: 'money', default: '50000.00' }),
    'treasury.currency_window_on_login': def({
        group: 'treasury',
        scope: 'personal',
        type: 'switch',
        default: true,
    }),
    /** OQ-16-05 — was PO_APPROVAL_LIMIT ('25000.00'). BR-PUR-01-02's setting. */
    'treasury.po_approval_limit': def({
        group: 'treasury',
        scope: 'org',
        type: 'money',
        default: '25000.00',
        extension: true,
    }),
    /** OQ-16-05 / Q-FIN-04-1 — was PAYMENT_APPROVAL_LIMIT ('25000.00'). */
    'treasury.payment_approval_limit': def({
        group: 'treasury',
        scope: 'org',
        type: 'money',
        default: '25000.00',
        extension: true,
    }),
    /**
     * FX-01 (bug triage, Oct 2026) — how often the `fx.refresh` job refreshes
     * every Live API currency row for this company, in minutes. 0 switches the
     * job off for the company; the Fetch Live Rates button keeps working
     * either way. The fan-out runs every 15 minutes, so a value below 15
     * behaves like 15 and anything else is honoured to the nearest pass.
     */
    'fx.auto_refresh_minutes': def({
        group: 'treasury',
        scope: 'org',
        type: 'number',
        default: 30,
        min: 0,
        max: 1440,
        extension: true,
    }),
    /**
     * DELEGATED RULING D12 — "the POS refund approval threshold is configurable
     * … default 200.00 base currency. The spec demands the approval but names no
     * number."
     *
     * WHAT THE SPEC ACTUALLY SAYS, because the number is the only part D12 had
     * to invent. SAL-06 §9: "What a cashier may do that a supervisor may not —
     * REFUNDS ABOVE A VALUE, voiding a completed sale, closing a shift with a
     * discrepancy — is a permission on the role (PART 16)." SAL-06 §15 open
     * question 2: "Does a refund need a supervisor? Proposed default: yes, above
     * a value limit; FREE BELOW IT." So the approval is a permission
     * (`sales.approve`, PART 16.3 — "Approve is also what expresses an approval
     * requirement … No separate mechanism is needed"), the gate is a value, and
     * the value is the one thing nobody wrote down.
     *
     * STRICTLY ABOVE, and the spec is explicit about the boundary on its own
     * sibling limit — PUR-01 §15: "An order exactly at the limit. The check is
     * strictly 'greater than' … That must be stated explicitly, because it is
     * the kind of boundary that gets built wrongly." A refund of exactly the
     * threshold is the cashier's. Enforced in `PosService.refund`.
     *
     * ── WHY THE KEY SAYS `pos.` AND THE GROUP SAYS `treasury` ─────────────────
     *
     * This is the first key in the registry whose prefix is not its group, so it
     * is worth saying why rather than leaving a reader to think it a slip. The
     * KEY is the ruling's, verbatim — `pos.refund_approval_threshold` — and a
     * ruling's identifier is not the build's to tidy. The GROUP is where the
     * other two approval ceilings already live: `treasury.po_approval_limit` and
     * `treasury.payment_approval_limit`, both money limits on who may commit the
     * company past a number. BR-PERM-14 proposed Invoices & Sales for the first
     * of them and the build put it in Treasury & Currency — "the group a money
     * ceiling belongs to" (packages/contracts/src/purchase-orders.ts) — so a
     * third one joining them is the settled reading, and an administrator doing
     * an authority review finds all three in one place instead of two.
     *
     * `group` is an explicit field on every row and nothing anywhere derives it
     * from the key, so the divergence costs nothing mechanical.
     *
     * BASE CURRENCY, so no rate question exists: D29 makes every till amount
     * base currency ("stock voucher currency → base currency everywhere"), and
     * this threshold is base currency too. Unlike the purchase-order limit —
     * which has to divide a foreign total by the order's rate before it may be
     * compared (BR-PUR-01-03/FIN-05) — the two sides here are already in the
     * same currency and a conversion step would be a bug, not a safeguard.
     */
    'pos.refund_approval_threshold': def({
        group: 'treasury',
        scope: 'org',
        type: 'money',
        default: '200.00',
        extension: true,
    }),
    /**
     * DELEGATED RULING A21 — "the deposit debits the bank account named by a new
     * `treasury.deposit_account` setting (default: the first postable bank
     * leaf)".
     *
     * SAL-06 §12 lists "Drawer banked at close | Bank | 1,412.00" among "two
     * entries the prototype does not write and must", and the debit side is the
     * bare word **Bank** — no code, no picker, nothing on the register (which
     * carries only a code, a name and a warehouse) and nothing in §5h's close
     * dialog. The chart has two bank leaves. This row is the missing answer.
     *
     * THE DEFAULT IS THE EMPTY STRING, AND THAT IS A VALUE, NOT AN ABSENCE. The
     * ruling's default — "the first postable bank leaf" — is a fact about the
     * TENANT'S chart, and this registry can only hold constants; writing '1112'
     * here would be a constant that is wrong for any company that renumbered.
     * So blank means "work it out at posting time", and `resolveDepositAccount`
     * (apps/api/src/platform/deposit-account.ts) is the one place that rule
     * lives. `accountCode: 'optional'` is what tells the save-time check that a
     * blank is legitimate here and a typo everywhere else.
     */
    'treasury.deposit_account': def({
        group: 'treasury',
        scope: 'org',
        type: 'text',
        default: '',
        maxLength: 20,
        accountCode: 'optional',
        extension: true,
    }),
    // ── Checks — 5 ────────────────────────────────────────────────────────────
    //
    // ── SPEC ROW ABSENT: `checks.reminder_method` ──────────────────────────────
    //
    // Retired by delegated ruling D15 (26 Aug 2026) — "notifications → in-app
    // for everything; email only for invitations, password resets, and
    // statements a user explicitly sends." §15.6.7's Checks group lists a sixth
    // row, Reminder method (Email / In-app / Both, default Both), and ADM-04 §12
    // is where cheque reminders were given a delivery choice. D15 removed the
    // email arm of that choice from the product: `NotificationRaiser` holds no
    // mailer (structurally, not by a guard — see its header and
    // `apps/api/test/email-census.spec.ts`), and the `check_reminder` template is
    // gone from the type layer, the renderers, the mirror and the database CHECK.
    //
    // WHY THE ROW GOES RATHER THAN COLLAPSING TO ONE OPTION. A setting offering
    // one value is not a setting — it is a control that cannot change anything,
    // occupying a row on ADM-01 and inviting a reader to believe a decision is
    // theirs to make. The in-app reminder is unconditional now, which is what the
    // product already does; saying so by absence is more honest than a select
    // with nothing to select. Deleting the row is the reversible half of D15: the
    // definition below is what a future ruling restores, in one place.
    //
    // SO THIS BUILD CARRIES 32 OF §15.6.7's 33 ROWS, DELIBERATELY. That is a
    // spec-versus-build deviation under D15's authority, not drift, and the count
    // is asserted rather than described — `SPEC_SETTING_KEYS` is pinned at 32 in
    // `apps/api/test/settings.e2e.spec.ts`, with this ruling as the reason.
    //
    // FOR WHOEVER REMOVES THE NEXT ROW — THE DICTIONARY GUARD ONLY CATCHES HALF
    // OF WHAT YOU ORPHAN, AND THE HALF IT MISSES IS THE BIGGER ONE. Removing a
    // row orphans two kinds of key, and `i18n-key-existence.test.ts`'s REVERSE
    // check sees only the first:
    //
    //   BESPOKE keys, named from ONE site — here `banking.reminderMethod.*`, read
    //   by the banking overview. That site was the sole contributor of the
    //   template head `banking.reminderMethod.`, so deleting it took the head
    //   away and all three keys went red by name. Loud, and it closes when the
    //   dictionary entries go.
    //
    //   REGISTRY-SHAPED keys — `settings.label.<key>`, `settings.hint.<key>`,
    //   `settings.opt.<key>.<option>`. The heads `settings.label.`,
    //   `settings.hint.` and `settings.opt.` are contributed by the settings
    //   screen and survive on every OTHER row, so the guard still counts these as
    //   named. They go silently: five keys in two languages describing a setting
    //   that does not exist, and nothing anywhere turns red.
    //
    // So the removal list is read off THIS FILE, never off whatever the guard
    // reported. Five of the eight keys D15's removal orphaned would have been
    // left behind by a tidy-up that trusted the red.
    //
    // STORED ROWS ARE NOT DELETED, AND NO MIGRATION ACCOMPANIES THIS. A tenant
    // seeded before today still holds an `org_settings` row for the key. It is
    // inert: every read path enumerates `ALL_SETTING_KEYS` (`settings.service.ts`
    // list, `SettingsReader`), so nothing asks for it, and a GET or PUT naming it
    // now gets the same 404 any unknown key gets. `app_user` has no DELETE grant
    // on `org_settings` at all — the 20260807700000 header states the rule this
    // follows: "rows live as long as the tenant — a setting is changed, never
    // removed." Deleting tenant rows for tidiness needs authority this wave does
    // not have, and buys nothing a reader can see.
    'checks.reminder_days': def({ group: 'checks', scope: 'org', type: 'number', default: 3, min: 0, max: 365 }),
    'checks.allow_early_deposit': def({ group: 'checks', scope: 'org', type: 'switch', default: false }),
    'checks.alert_out_of_range_number': def({ group: 'checks', scope: 'org', type: 'switch', default: true }),
    'checks.line_count': def({ group: 'checks', scope: 'role', type: 'number', default: 12, min: 1, max: 200 }),
    'checks.scanner': def({
        group: 'checks',
        scope: 'personal',
        type: 'enum',
        options: ['none', 'canon_dr', 'panini_ideal'],
        default: 'none',
    }),
    /**
     * FIN-12 §5h fixed as its defects list demands: the low-balance alert
     * threshold is a COMPANY setting, compared in the base currency
     * (BR-FIN-12-03), not a session scribble.
     */
    'checks.low_balance_alert_threshold': def({
        group: 'checks',
        scope: 'org',
        type: 'money',
        default: '10000.00',
        extension: true,
    }),
    // ── Invoices & Sales — 4 (+5 extensions) ──────────────────────────────────
    /** BR-MD-01-10a / BR-ADM-01-14 — the cap the sellers keep hitting. */
    'invoicing.max_discount_pct': def({ group: 'invoicing', scope: 'org', type: 'percent', default: '15' }),
    'invoicing.default_invoice_type': def({
        group: 'invoicing',
        scope: 'role',
        type: 'enum',
        options: ['cash', 'credit', 'last_used'],
        default: 'last_used',
    }),
    'invoicing.default_voucher_type': def({
        group: 'invoicing',
        scope: 'role',
        type: 'enum',
        options: ['receipt', 'payment', 'ask'],
        default: 'ask',
    }),
    'invoicing.journal_line_count': def({
        group: 'invoicing',
        scope: 'role',
        type: 'number',
        default: 20,
        min: 1,
        max: 200,
    }),
    /**
     * MD-09 §9 — "The organization setting Payment terms decides whether the
     * concept exists at all for this tenant." Off: no term is asked for on a
     * partner, the due date equals the document date, and the terms screen is
     * not offered. On (the default — the backfill may already have minted
     * terms from `payment_terms_days`): the due date is worked out from the
     * term and stays editable while the document is a draft (BR-MD-09-03).
     */
    'invoicing.payment_terms': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: true,
        extension: true,
    }),
    /**
     * INV-L1 (bug triage, Oct 2026) — the Bonus column on the sales grids: a
     * free quantity given with a line, leaving stock with the sold quantity at
     * zero revenue. OFF by default: most businesses never give free units, and
     * a column nobody uses is noise. Off, the column is not drawn and every
     * line stores 0.
     */
    'invoicing.line_bonus': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    /**
     * INV-L2 (bug triage, Oct 2026) — whether the per-line Disc. column is
     * drawn on the sales grids. Display only: off, the column is hidden and a
     * line's discount saves as 0; the document discount (F-2) is untouched.
     */
    'invoicing.line_discount': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: true,
        extension: true,
    }),
    /** EC-19-73 — was DEFAULT_TAX_PCT ('15'): the rate a document opens with. */
    'invoicing.default_vat_pct': def({
        group: 'invoicing',
        scope: 'org',
        type: 'percent',
        default: '15',
        extension: true,
    }),
    // SAL-01 §8a — the four quotation feature switches, all off by default.
    // Only internal approval is ENFORCED today (send needs sales.approve when
    // on); the other three are stored switches whose consuming features are
    // TODO(quotation-features) where each would land.
    'invoicing.quote_multi_tier_pricing': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    'invoicing.quote_internal_approval': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    'invoicing.quote_customer_signature': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    'invoicing.quote_multi_currency': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    /**
     * FIN-08 §5b + BR-SAL-04-01 + AP-10 — the clearance model, as a company
     * switch.
     *
     * "Some countries require every sales invoice to be sent to the tax
     * authority BEFORE it is given to the customer" (§4.13). Turned ON, a
     * posted invoice cannot be sent to the customer until the authority
     * returns Accepted, and the amber banner says so; turned OFF — the
     * shipped default, because most companies are not in such a country —
     * the invoice is issued first and reported after, and neither clearance
     * banner is drawn at all (BR-FIN-08-04).
     *
     * It is a SETTING and not an adapter constant on purpose: whether a
     * company's country clears is a fact about the company, and it is the
     * one thing on this screen that changes what a user may do. The country's
     * FORMAT, signature and QR encoding stay behind the adapter interface,
     * where no amount of settings can reach them (BR-FIN-08-01).
     */
    'invoicing.einvoice_clearance_required': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    /**
     * FIN-08 §5a / Configuration tab — which jurisdiction adapter signs.
     * `mock` is the shipped example, marked as an example on screen; the two
     * real ones are declared here so the vocabulary is closed and an
     * administrator can see what exists, and refuse with a plain sentence
     * until they are implemented (TODO(zatca), TODO(ps-jo)).
     */
    'invoicing.einvoice_jurisdiction': def({
        group: 'invoicing',
        scope: 'org',
        type: 'enum',
        options: ['mock', 'zatca', 'ps_jo'],
        default: 'mock',
        extension: true,
    }),
    /**
     * FIN-08 §2 Prerequisites — "the company's tax registration number must be
     * entered". Its LABEL comes from the adapter (BR-FIN-08-10), which is why
     * the key is neutral: `Tax ID` in one country, `Tax Registration No.` in
     * the next.
     */
    'invoicing.einvoice_tax_id': def({
        group: 'invoicing',
        scope: 'org',
        type: 'text',
        default: '',
        maxLength: 40,
        extension: true,
    }),
    /**
     * DELEGATED RULING A22 — "a company `invoicing.service_revenue_account`
     * setting (default 4020), read when a line has no item".
     *
     * WHAT IT REPLACES, AND WHY THE THING IT REPLACES WAS UNREACHABLE. MD-04 §5
     * field 5 is "Default income account … which account a sale of an item in
     * this group is credited to", with a four-rung cascade the screen states in
     * its own words: the ITEM, else the item's own CATEGORY, else the nearest
     * ANCESTOR category, else the COMPANY DEFAULT. A project-billing line has no
     * item at all — `generate()` builds `{ description, qty, unitPrice }` where
     * the description is a date, a person and a task title — so rungs 1 to 3
     * cannot fire, and rung 4 is a single company-wide value that cannot be
     * goods revenue and service revenue at the same time. Building the whole
     * cascade correctly would have left PRJ-04 §12 exactly as unhonoured as it
     * was: "Project work is Service Revenue, not Sales Revenue — selling time is
     * not selling goods, and the two must be separable in the profit and loss."
     *
     * This row is the SECOND company value that makes rung 4 answerable. It is
     * read for exactly one condition — a line with no item — and for nothing
     * else; a service ITEM sold on an ordinary invoice still follows its item,
     * which is the cascade's business and not this row's.
     *
     * 4020 Service Revenue is seeded and postable in the FIN-01 chart, so the
     * default works on a fresh company without anybody configuring anything.
     */
    'invoicing.service_revenue_account': def({
        group: 'invoicing',
        scope: 'org',
        type: 'text',
        default: '4020',
        maxLength: 20,
        accountCode: 'required',
        extension: true,
    }),
    /**
     * Wave 4D — the CASH INVOICE's default account: where the money of a cash
     * invoice goes when the invoice itself picks none. 1111 is the seeded till
     * cash leaf under 1110. Blank is a value (`accountCode: 'optional'`): "no
     * default" — a cash invoice must then pick its own account, and one that
     * does not is refused (`SALES_MESSAGES.cashAccountRequired`). The account
     * must be a cash account either way (receipts' `cashAccountsOnly`).
     * Row 20261028220000.
     */
    'invoicing.default_cash_account': def({
        group: 'invoicing',
        scope: 'org',
        type: 'text',
        default: '1111',
        maxLength: 20,
        accountCode: 'optional',
        extension: true,
    }),
    /**
     * ── THE BULK STATEMENT RUN — delegated ruling D15's opt-in ────────────────
     *
     * D15 (26 Aug 2026): "…email only for invitations, password resets, and
     * statements a user explicitly sends… This unblocks the bulk-statement job
     * as an ADM-01 opt-in, DEFAULT OFF."
     *
     * DEFAULT OFF IS THE RULING, NOT A CAUTIOUS CHOICE, and the reasons the
     * JOBS-AUTO wave recorded before stopping are the reasons it must stay off
     * until somebody decides otherwise on a screen:
     *
     *   - THE SPEC NEVER ASKS FOR IT. CRM-04 has 21 business rules and not one
     *     mentions a bulk or scheduled send; §5's "Send Statement" is
     *     single-partner throughout.
     *   - CRM-04 §9's own argument runs against unattended sending: "Send
     *     Statement is the one outward-facing action, and it carries its own
     *     permission because it puts a document in front of the customer." A
     *     SCHEDULE HOLDS NO PERMISSION. Turning this on is the act in which a
     *     person with `admin` lends the schedule their intent — which is why the
     *     switch, and not the job, is the "explicitly sends" half of D15.
     *   - DECISION.md §2.12 requires "one-click unsubscribe on anything bulk"
     *     and nothing implements one. A run reaching every customer of every
     *     tenant BY DEFAULT would be bulk mail with no way out; a run a company
     *     switched on for its own customers is that company's correspondence.
     *
     * SCOPE is `org` and not `owner`: ADM-01's padlock ladder (§15.6.5) already
     * lets an Owner lock an org row, so "only the Owner may change it" is
     * expressible without a scope that hides it from the Administrator who
     * actually runs the office.
     */
    'invoicing.statement_auto_send': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    /**
     * WHICH DAY THE RUN GOES OUT — "on the configured cadence", made a number
     * somebody can see rather than a constant in a job file.
     *
     * MAX 28, AND THAT BOUND IS THE WHOLE DESIGN OF THIS ROW. A run set to the
     * 30th would silently never fire in February — a failure with no error, no
     * red anything, and eleven months of evidence that it works. The bound makes
     * the impossible configuration unreachable instead of making the job guess
     * what the 30th means in a 28-day month, which is the guess every "clamp to
     * month end" implementation gets to make differently from the next one.
     *
     * MIN 1 rather than 0: this is a day of the month, and there is no 0th.
     */
    'invoicing.statement_auto_send_day': def({
        group: 'invoicing',
        scope: 'org',
        type: 'number',
        default: 1,
        min: 1,
        max: 28,
        extension: true,
    }),
    /**
     * Wave 5B (ERP gap analysis, Oct 2026) — whether a delivery note may be
     * raised to a customer with NO sales order behind it (a counter hand-over,
     * a sample, a replacement on a service call). Off by default: BR-SAL-03-11
     * ("a real reference") stays the rule until a company opens the door, and
     * the create route refuses with `DELIVERY_REFUSALS.directDeliveryDisabled`
     * while it is off. Filed under the sales group's heading; the key keeps
     * the `sales.` prefix of the permission area it belongs to.
     */
    'sales.direct_delivery_enabled': def({
        group: 'invoicing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    // ── Purchasing — 2, both org, both extensions (wave 1C, Oct 2026) ─────────
    /**
     * The three-way match's tolerance band (BR-PUR-03-12 shipped strict to the
     * cent and left the band "an open question until org settings exist" —
     * this is that setting). A bill whose figure sits within this percentage
     * of what was ordered or received is MATCHED; above it is a variance that
     * a purchasing.approve holder must accept, and under the four-eyes rule
     * the accepter may not also post. 0 keeps the shipped strict comparison.
     */
    'purchasing.bill_variance_tolerance_pct': def({
        group: 'purchasing',
        scope: 'org',
        type: 'percent',
        default: '0',
        extension: true,
    }),
    /**
     * BR-MD-09-03's fallback: a supplier bill whose supplier carries no MD-09
     * term used to fall due on its own date. This is the company's default
     * term for suppliers, in days after the bill date; the supplier's own term
     * still wins when there is one, and `invoicing.payment_terms` off still
     * means due on issue.
     */
    'purchasing.default_payment_term_days': def({
        group: 'purchasing',
        scope: 'org',
        type: 'number',
        default: 30,
        min: 0,
        max: 365,
        extension: true,
    }),
    /**
     * Wave 5D — WHERE A DEBIT NOTE'S PRICE DIFFERENCE LANDS. PUR-04 §12's last
     * row is "(balancing difference to purchase price variance)" and §15 asks
     * for "a supplier-claims account" on a value-only return; the seeded chart
     * has no such account, so 5050 took both. This row lets a company name the
     * account instead — a returns / supplier-claims expense leaf — and a
     * document may override it per note (`returnsAccountId`).
     *
     * IT REACHES EXACTLY ONE LEG. The override moves ONLY the balancing leg of
     * the debit note's entry — the price-difference leg, which on a value-only
     * return is the whole net claim. 2111 Trade Payables (what we owe), 2120
     * VAT and the per-item inventory legs are never redirected: redirecting
     * the payable would misstate the supplier's balance, and redirecting the
     * inventory leg would break 1131 = Σ stock_value.
     *
     * Blank is a value, not an absence (the deposit-account precedent): it
     * means "5050, the chart's own variance account". `accountCode: 'optional'`
     * is what admits the blank at save time.
     */
    'purchasing.returns_account': def({
        group: 'purchasing',
        scope: 'org',
        type: 'text',
        default: '',
        maxLength: 20,
        accountCode: 'optional',
        extension: true,
    }),
    /**
     * Wave 5E — whether a shipment may be raised WITHOUT a purchase order
     * (supplier and lines typed on the import itself). BR-PUR-06-01's
     * "always from a purchase order" stays the default; a company that buys
     * abroad on a pro-forma and never raises an order switches this on.
     * Row 20261027350000; `IMPORT_REFUSALS.directImportDisabled` at the door.
     */
    'purchasing.direct_import_enabled': def({
        group: 'purchasing',
        scope: 'org',
        type: 'switch',
        default: false,
        extension: true,
    }),
    // ── Commissions — 2, both OWNER ONLY (invisible to everybody else) ────────
    'commissions.on_collection': def({ group: 'commissions', scope: 'owner', type: 'switch', default: true }),
    'commissions.on_collection_checks': def({
        group: 'commissions',
        scope: 'owner',
        type: 'switch',
        default: false,
    }),
    // ── Inventory & Transfers — 6 (+2 extensions) ─────────────────────────────
    'inventory.collect_items_at_conversion': def({
        group: 'inventory',
        scope: 'org',
        type: 'switch',
        default: true,
    }),
    'inventory.cancel_merge_builds': def({ group: 'inventory', scope: 'org', type: 'switch', default: false }),
    /** ON = warehouse transfers must move in full (no partial receive). */
    'inventory.cancel_partial_transfer': def({
        group: 'inventory',
        scope: 'org',
        type: 'switch',
        default: false,
    }),
    'inventory.default_transfer_mode': def({
        group: 'inventory',
        scope: 'org',
        type: 'enum',
        options: ['instant', 'two_step'],
        default: 'instant',
    }),
    'inventory.allow_partial_transfers': def({
        group: 'inventory',
        scope: 'org',
        type: 'switch',
        default: true,
    }),
    /** "The only approval switch anywhere in the product's settings" — OQ-16-07: ships off. */
    'inventory.require_voucher_approval': def({
        group: 'inventory',
        scope: 'org',
        type: 'switch',
        default: false,
    }),
    /**
     * Owner ruling (27 Sep 2026) — when ON, posting a STANDALONE purchase invoice
     * (no purchase order behind it) adds its goods straight to stock, instead of
     * the Purchase Order -> Goods Receipt -> stock flow. An order-backed bill is
     * unaffected.
     *
     * ON BY DEFAULT since wave 1C (ERP gap analysis, Oct 2026). It shipped OFF,
     * on the §3.3.1 principle that stock changes only when goods physically
     * move; the gap review found the consequence — a stock-tracked item on a
     * no-order bill was EXPENSED to 5010 and never reached inventory, so stock
     * value and the purchases figure were both wrong for every company that
     * works bill-first and never found the switch. The default flipped
     * (20261026110000 updates the rows still at the shipped default); a company
     * that wants the strict receipt-only flow turns it off deliberately. With
     * it on, a stock line on a standalone bill needs its receiving warehouse
     * (`BILL_REFUSALS.warehouseRequired`).
     */
    'inventory.bill_writes_stock': def({
        group: 'inventory',
        scope: 'org',
        type: 'switch',
        default: true,
        extension: true,
    }),
    /** OQ-16-05 — was COUNT_VARIANCE_TOLERANCE_DEFAULT (10): INV-05 §5.7's amber band. */
    'inventory.count_variance_tolerance': def({
        group: 'inventory',
        scope: 'org',
        type: 'number',
        default: 10,
        min: 0,
        max: 100000,
        extension: true,
    }),
    // ── Stock voucher accounts — 12, all org, all extensions ──────────────────
    //
    // INV-08 §12: "The account pair depends on the REASON, which is why the
    // reason is required." One account CODE per reason — the NON-INVENTORY leg;
    // the inventory leg follows the item's own inventory account (MD-03) or
    // 1131. Defaults are the build's former in-code map
    // (ADJUSTMENT_REASON_ACCOUNTS, now retired), resolved against the seeded
    // FIN-01 chart the chart-wins way. Values validate here as short codes;
    // the settings service additionally refuses a code that names no postable,
    // active account in the tenant's chart — an accountant edits these without
    // a developer, and a typo must fail at save, not at posting.
    //
    // Stock In (INV-08 §5.2's six):
    /** "First loading of stock into a new system" → 3040 Opening Balance Equity. */
    'stock_accounts.opening_balance': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '3040',
        maxLength: 20,
        extension: true,
    }),
    /** "Goods discovered that the book did not know about" → 5030 Stock Adjustments. */
    'stock_accounts.found_stock': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5030',
        maxLength: 20,
        extension: true,
    }),
    /**
     * A hand-typed "production output" voucher — goods with no job behind them.
     * NOT WIP: a real work order's output relieves 1134 through MFG-02 §12
     * posting 4; crediting WIP here would strand a balance no job will empty.
     */
    'stock_accounts.production_output': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5030',
        maxLength: 20,
        extension: true,
    }),
    /** "A customer brought something back with no credit note" → 5030. */
    'stock_accounts.customer_return_non_invoice': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5030',
        maxLength: 20,
        extension: true,
    }),
    /** "Goods received for nothing" → 4040 Other Income (the donation mirror). */
    'stock_accounts.gift_received': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '4040',
        maxLength: 20,
        extension: true,
    }),
    /** Both directions' "Other" share the one reason value, so one row. */
    'stock_accounts.other': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5030',
        maxLength: 20,
        extension: true,
    }),
    // Stock Out (INV-08 §5.2's seven; Other is shared above):
    /** "Goods that can no longer be sold" → 5030 (INV-08 §12's write-off row). */
    'stock_accounts.damaged_expired': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5030',
        maxLength: 20,
        extension: true,
    }),
    /**
     * INV-08 §12 posts internal use to "Internal consumption expense" — an
     * account the seeded chart does not carry, so the shipped default stays
     * 5030 (the chart-wins ruling) and a company that wants the dedicated
     * account adds it to the chart and points this row at it.
     */
    'stock_accounts.internal_use': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5030',
        maxLength: 20,
        extension: true,
    }),
    /** "Given away to promote" → 5150 Marketing & Advertising (INV-08 §12). */
    'stock_accounts.samples_marketing': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5150',
        maxLength: 20,
        extension: true,
    }),
    /**
     * Materials issued with NO work order at all — MFG-04 §5.5 classifies that
     * as an INDIRECT issue, an expense, never work in progress (BR-MFG-04-16).
     */
    'stock_accounts.production_usage': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5030',
        maxLength: 20,
        extension: true,
    }),
    /** "Given to charity" → 5150 ("Marketing or donation expense", INV-08 §12). */
    'stock_accounts.donation': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5150',
        maxLength: 20,
        extension: true,
    }),
    /** "Missing, cause unknown" → 5030 (INV-08 §12's shrinkage row). */
    'stock_accounts.shrinkage_loss': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5030',
        maxLength: 20,
        extension: true,
    }),
    /**
     * PRJ-01 (bug triage 2026-10): material ISSUED TO A PROJECT — cable and
     * cabinets leaving the warehouse for an installation site → 5040 Cost of
     * Sales, the postable COGS leg, because what a customer's project consumes
     * is the cost of what it sells. The thirteenth row; seeded into existing
     * tenants by 20261012231000.
     */
    'stock_accounts.project_issue': def({
        group: 'stock_accounts',
        scope: 'org',
        type: 'text',
        default: '5040',
        maxLength: 20,
        extension: true,
    }),
    // ── Manufacturing — 3 (PART 9 §9.1.3's "organization setting" mechanism) ──
    //
    // All three carry `extension: true`, and the flag means what it means
    // everywhere else on this registry: they are settings ANOTHER PART promotes
    // onto ADM-01, not members of PART 15.6's own 33. §9.1.3 asks for them;
    // §15.6.7 has never listed them, and the settings suite pins that count.
    //
    // §9.1.3 names exactly three capability differences inside manufacturing
    // that are a COMPANY choice rather than a permission: "backflush on or off,
    // the labor rate, the planning horizon". These are those three, and there
    // are no others — every remaining difference in that part is a permission.
    //
    /**
     * §9.4's ONE labour rate: "Labor is `total routing minutes × one
     * company-wide rate per minute`. That rate is 0.75 per minute in the shipped
     * data. There is NO per-workstation rate and NO per-operation rate anywhere
     * in the product" (BR-MFG-02-20). MFG-01 field 23 prints it under the labour
     * figure as `@ 0.75/min`.
     *
     * Money, so it crosses as a decimal STRING (§2.1) — and the default is the
     * shipped 0.75, which is what makes the §9.4 worked example reproduce.
     */
    'manufacturing.labor_rate_per_minute': def({
        group: 'manufacturing',
        scope: 'org',
        type: 'money',
        default: '0.75',
        extension: true,
    }),
    /**
     * MFG-02 field 44: "Its starting value is an organization setting." The
     * switch itself is per work order and may be flipped mid-job; this is what a
     * new job starts at.
     */
    'manufacturing.backflush_default': def({
        group: 'manufacturing',
        scope: 'org',
        type: 'switch',
        default: true,
        extension: true,
    }),
    /**
     * MFG-03 §5.4's first required parameter, defaulting to 8 weeks — "bounds
     * the run and matches the 21-day chart". The other two (warehouse, include
     * forecasts) are per-run choices on the MRP card, not company policy, so
     * they are request parameters rather than settings.
     */
    'manufacturing.planning_horizon_weeks': def({
        group: 'manufacturing',
        scope: 'org',
        type: 'enum',
        options: ['4', '8', '12'],
        default: '8',
        extension: true,
    }),
    // ── Data & Backups — 2, both OWNER ONLY ───────────────────────────────────
    'data.backup_email': def({ group: 'data', scope: 'owner', type: 'switch', default: true }),
    'data.backup_frequency': def({
        group: 'data',
        scope: 'owner',
        type: 'enum',
        options: ['daily', 'weekly', 'manual'],
        default: 'daily',
    }),
    // ── Security & Locks — 2, both OWNER ONLY ─────────────────────────────────
    'security.block_future_dated': def({ group: 'security', scope: 'owner', type: 'switch', default: true }),
    'security.lock_closed_periods': def({ group: 'security', scope: 'owner', type: 'switch', default: true }),
    // ── Interface — 5, all personal ───────────────────────────────────────────
    'interface.show_chat': def({ group: 'interface', scope: 'personal', type: 'switch', default: true }),
    'interface.confirm_close_tab': def({ group: 'interface', scope: 'personal', type: 'switch', default: true }),
    'interface.sync_manual_date': def({ group: 'interface', scope: 'personal', type: 'switch', default: false }),
    'interface.default_date': def({
        group: 'interface',
        scope: 'personal',
        type: 'enum',
        options: ['today', 'last_used', 'empty'],
        default: 'today',
    }),
    'interface.show_dashboard_charts': def({
        group: 'interface',
        scope: 'personal',
        type: 'switch',
        default: true,
    }),
    // ── Printing — 11, all org, all extensions (INV-H6 / UB-03) ───────────────
    //
    // The options the Print button honours (`apps/web/src/lib/print-document.ts`
    // applies them on top of `renderDocumentHtml`'s output, so the shared
    // template stays one template). Every default reproduces the paper the
    // product printed before these rows existed: no header or footer text, the
    // company block drawn, every column drawn, A4, the reader's own language.
    //
    // The three free-text rows are PROSE, not codes — a slogan, a registration
    // line, an IBAN block — so their cap is wider than the registry's usual
    // 200. They are escaped like everything else that lands on a document.
    /** Printed under the company block on every document; blank = nothing. */
    'print.header_text': def({ group: 'print', scope: 'org', type: 'text', default: '', maxLength: 500, extension: true }),
    /** Printed above the "Printed on" line on every document; blank = nothing. */
    'print.footer_text': def({ group: 'print', scope: 'org', type: 'text', default: '', maxLength: 500, extension: true }),
    /**
     * The company block (name, tax id, address) at the top. OFF is for a
     * company that prints on pre-printed letterhead and must not print its
     * name twice.
     */
    'print.show_logo': def({ group: 'print', scope: 'org', type: 'switch', default: true, extension: true }),
    'print.show_item_code': def({ group: 'print', scope: 'org', type: 'switch', default: true, extension: true }),
    'print.show_discount_column': def({ group: 'print', scope: 'org', type: 'switch', default: true, extension: true }),
    /** The tax line in the totals rail — a company outside VAT prints none. */
    'print.show_tax_column': def({ group: 'print', scope: 'org', type: 'switch', default: true, extension: true }),
    /** A Warehouse field in the header block when the caller names one. */
    'print.show_warehouse': def({ group: 'print', scope: 'org', type: 'switch', default: false, extension: true }),
    'print.show_bank_details': def({ group: 'print', scope: 'org', type: 'switch', default: false, extension: true }),
    /** The block `print.show_bank_details` prints — bank, account, IBAN, as typed. */
    'print.bank_details': def({ group: 'print', scope: 'org', type: 'text', default: '', maxLength: 500, extension: true }),
    /** `@page { size }` — A4, A5, or an 80 mm thermal roll. */
    'print.paper': def({
        group: 'print',
        scope: 'org',
        type: 'enum',
        options: ['A4', 'A5', 'thermal80'],
        default: 'A4',
        extension: true,
    }),
    /** The document's language: the reader's own (today's behaviour), or fixed. */
    'print.language': def({
        group: 'print',
        scope: 'org',
        type: 'enum',
        options: ['follow_ui', 'en', 'ar'],
        default: 'follow_ui',
        extension: true,
    }),
    // ── ADM-08 Personalization — 6, all personal, none on ADM-01 ──────────────
    //
    // §15.4's six cards, in the spec's own order, with the spec's own defaults.
    // Every one is `scope: 'personal'`, which buys three of its rules outright:
    // BR-ADM-08-02 ("must belong to one person only"), BR-ADM-08-03 ("must
    // survive signing out and must follow the person to another computer" —
    // `user_settings` is a server table, which is the [decide at build time]
    // this rule left open, decided the way it proposed), and BR-ADM-08-12
    // ("changes here must NOT be written to the Audit Log" — the settings
    // service never audits a personal override; BR-ADM-01-09 already said so).
    //
    // Values are the spec's ENUM NAMES, not the pixel numbers behind them. The
    // numbers belong to the stylesheet: storing `15` would make a redesign a
    // data migration, and would let a value exist that no card can select.
    'appearance.theme': def({
        group: 'appearance',
        scope: 'personal',
        type: 'enum',
        // BR-ADM-08-05: "exactly Light and Dark. A follow-my-device option must be
        // added DELIBERATELY if it is wanted." It is not here, so it is not built.
        options: ['light', 'dark'],
        default: 'light',
        personalization: true,
    }),
    /** §5 field 2 — 13.5 / 15 / 16.5 / 18.5 px, applied to the ROOT (BR-ADM-08-06). */
    'appearance.text_size': def({
        group: 'appearance',
        scope: 'personal',
        type: 'enum',
        options: ['small', 'default', 'large', 'xl'],
        default: 'default',
        personalization: true,
    }),
    /** §5 field 3. Forced to the Arabic face in Arabic (BR-ADM-08-11). */
    'appearance.font_style': def({
        group: 'appearance',
        scope: 'personal',
        type: 'enum',
        options: ['jakarta', 'plex', 'source_serif', 'system'],
        default: 'jakarta',
        personalization: true,
    }),
    /** §5 field 4 — 300 / 400 / 500, base body weight only. */
    'appearance.font_weight': def({
        group: 'appearance',
        scope: 'personal',
        type: 'enum',
        options: ['light', 'regular', 'medium'],
        default: 'regular',
        personalization: true,
    }),
    /** §5 field 5 — 9 / 14 / 19 px of cell padding, and NOTHING else (BR-ADM-08-07). */
    'appearance.density': def({
        group: 'appearance',
        scope: 'personal',
        type: 'enum',
        options: ['compact', 'cozy', 'comfortable'],
        default: 'cozy',
        personalization: true,
    }),
    /** §5 field 6 — the five named swatches (BR-ADM-08-09). */
    'appearance.accent': def({
        group: 'appearance',
        scope: 'personal',
        type: 'enum',
        options: ['signal_blue', 'indigo', 'teal', 'green', 'amber'],
        default: 'signal_blue',
        personalization: true,
    }),
};
export const ALL_SETTING_KEYS = Object.keys(SETTINGS_REGISTRY);
/** The spec's 33 (registry minus extensions) — pinned by the seed test. */
export const SPEC_SETTING_KEYS = ALL_SETTING_KEYS.filter((key) => !SETTINGS_REGISTRY[key].extension &&
    !SETTINGS_REGISTRY[key].personalization);
/**
 * WHAT ADM-01 DRAWS — everything except ADM-08's six.
 *
 * The two screens share one storage mechanism and one API deliberately (a
 * personal override is a `user_settings` row either way), and they are still
 * two screens: see `personalization` on SettingDef for the rule that decides
 * which side a setting falls on, and why a lockable rail is the wrong home for
 * a text-size button.
 */
export const GENERAL_SETTING_KEYS = ALL_SETTING_KEYS.filter((key) => !SETTINGS_REGISTRY[key].personalization);
/** ADM-08's six, in the order §15.4 draws its cards. */
export const PERSONALIZATION_SETTING_KEYS = ALL_SETTING_KEYS.filter((key) => SETTINGS_REGISTRY[key].personalization === true);
export function isSettingKey(value) {
    return Object.hasOwn(SETTINGS_REGISTRY, value);
}
/** The twelve INV-08 §12 reason rows — every key in the stock_accounts group. */
export const STOCK_ACCOUNT_SETTING_KEYS = ALL_SETTING_KEYS.filter((key) => SETTINGS_REGISTRY[key].group === 'stock_accounts');
/** INV-H6's eleven — every key in the print group, in the registry's order. */
export const PRINT_SETTING_KEYS = ALL_SETTING_KEYS.filter((key) => SETTINGS_REGISTRY[key].group === 'print');
/**
 * The settings key holding a voucher reason's non-inventory account, or null
 * for a reason the registry does not know (the voucher service refuses those
 * long before posting — BR-INV-08-03's disjoint lists).
 */
export function stockAccountSettingKey(reason) {
    const key = `stock_accounts.${reason}`;
    return isSettingKey(key) ? key : null;
}
/**
 * Is this setting's value an account CODE, and may it be blank?
 *
 * THE ONE PLACE THE TWO SIGNALS ARE READ. The twelve `stock_accounts` rows
 * declare themselves by their GROUP (they predate the flag and there is no
 * reason to restate it twelve times); everything since declares itself with
 * `accountCode`. A caller that had to know both would be a caller that could
 * forget one — which is how the settings service ends up validating eleven of
 * twelve account rows and nobody notices.
 *
 * Returns null when the setting is not an account reference at all.
 */
export function accountCodeSetting(key) {
    const definition = SETTINGS_REGISTRY[key];
    if (definition.group === 'stock_accounts')
        return 'required';
    return definition.accountCode ?? null;
}
/** Every setting whose value names an account — the twelve, plus A21's and A22's. */
export const ACCOUNT_CODE_SETTING_KEYS = ALL_SETTING_KEYS.filter((key) => accountCodeSetting(key) !== null);
export function settingDef(key) {
    return SETTINGS_REGISTRY[key];
}
/** The per-key value schema — how every write is validated (BR-ADM-01 §14/15). */
export function settingValueSchema(key) {
    const definition = SETTINGS_REGISTRY[key];
    switch (definition.type) {
        case 'switch':
            return z.boolean();
        case 'number': {
            let schema = z.number().int();
            if (definition.min !== undefined)
                schema = schema.min(definition.min);
            if (definition.max !== undefined)
                schema = schema.max(definition.max);
            return schema;
        }
        case 'money':
            /*
             * ERR-FIN-12-04 — "typed rubbish in the threshold: every low-balance
             * alert silently disappears". The prototype's session-only box treated
             * anything unparseable as "no threshold at all". That control no longer
             * exists — the threshold is the checks.low_balance_alert_threshold
             * COMPANY setting (FIN-12's defects-list repair), and this arm is its
             * write gate: rubbish is refused with a sentence, never coerced to
             * "off", so the silent-disable failure mode has no producer. Pinned in
             * settings.e2e.spec.ts.
             */
            return MoneyStringSchema;
        case 'percent':
            return PctStringSchema;
        case 'date':
            return IsoDateSchema;
        case 'enum':
            return z.enum(definition.options);
        case 'text':
            return z.string().trim().max(definition.maxLength ?? 200);
    }
}
// ── The fixed sentences of ADM-01 ───────────────────────────────────────────
export const SETTINGS_MESSAGES = {
    /** ERR-ADM-01-03. */
    cycleEndBeforeStart: 'The end of the cycle must be after the start.',
    /** ERR-ADM-01-04. */
    discountOver100: 'A discount cannot be more than 100%.',
    /** ERR-ADM-01-05. */
    negativeNumber: 'This must be zero or more.',
    /** ERR-ADM-01-02's hover text — also the 403 for a row the caller may not set. */
    ownerOnly: 'Only the account owner can change this.',
    /** BR-ADM-01-04 — org/role rows are Owner/Administrator acts. */
    notYours: 'You do not have permission to change this setting.',
    /** BR-ADM-01-05 — an override on a locked setting is refused. */
    lockedSetting: 'This setting is locked by your organization.',
    /** Closed registry — an unknown key does not exist. */
    unknownSetting: 'No such setting.',
    /** A value failing its per-key schema. */
    badValue: 'That value is not valid for this setting.',
    /** Lock/unlock applies to org and role rows only (§15.6.5). */
    notLockable: 'Only organization and role settings can be locked or unlocked.',
    /**
     * INV-08 §12's settings table — a stock_accounts row must name a postable,
     * active account in the tenant's own chart, refused at save so an
     * accountant's typo never waits until a posting to surface.
     */
    notAnAccount: 'No postable, active account with this code exists in the chart of accounts.',
    /**
     * A21 — `treasury.deposit_account` pointed at the till the sales are taken
     * in. The entry would balance (DR 1111 / CR 1111 for the deposit, with the
     * difference still landing in 1119) and would mean nothing: no money left
     * the drawer, and the books would say a deposit happened. Refused at SAVE,
     * where the person who chose it is standing, rather than at the shift close,
     * where a cashier who chose nothing would meet it.
     */
    depositAccountIsTill: 'A drawer deposit cannot bank into the till it came from.',
};
// ── Wire shapes ─────────────────────────────────────────────────────────────
const SettingValueSchema = z.union([z.boolean(), z.number(), z.string()]);
const MessageSchema = z.object({ message: z.string() }).strict();
export const SettingRowSchema = z
    .object({
    key: z.string(),
    group: z.enum(SETTING_GROUPS),
    scope: z.enum(SETTING_SCOPES),
    type: z.enum(['switch', 'number', 'money', 'percent', 'date', 'enum', 'text']),
    options: z.string().array().optional(),
    /** The company value (org/role/owner) or the caller's own (personal). */
    value: SettingValueSchema,
    /** The registry default, so the UI can offer "reset". */
    default: SettingValueSchema,
    /** §15.6.5 — the padlock. Meaningful on org/role rows; personal rows are never locked. */
    locked: z.boolean(),
    /** True when the CALLER holds a personal override of this org/role row. */
    overridden: z.boolean(),
    /** The caller's effective value (override ?? company value). */
    effectiveValue: SettingValueSchema,
    updatedAt: z.string().nullable(),
    /** May the CALLER change the company value / the lock? (drives dimming) */
    canChange: z.boolean(),
})
    .strict();
export const UpdateSettingRequestSchema = z.object({ value: SettingValueSchema }).strict();
export const LockSettingRequestSchema = z.object({ locked: z.boolean() }).strict();
/**
 * The values every signed-in user's screens need to BEHAVE correctly —
 * org/role-scope values merged with the caller's own overrides, plus the
 * caller's personal settings. Owner-only values are NEVER in this map
 * (BR-ADM-01-02: invisible, not read-only) — their effects are enforced
 * server-side where they apply.
 */
export const EffectiveSettingsSchema = z.object({
    settings: z.record(z.string(), SettingValueSchema),
});
const keyParams = z.object({ key: z.string() }).strict();
export const orgSettingsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/settings',
        responses: {
            200: z.object({ settings: SettingRowSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Every setting the caller may SEE (admin.view; owner-only rows for the Owner alone — BR-ADM-01-02), with values, locks and the caller’s overrides.',
    },
    effective: {
        method: 'GET',
        path: '/api/settings/effective',
        responses: {
            200: EffectiveSettingsSchema,
            401: MessageSchema,
        },
        summary: 'The caller’s effective org/role/personal values — no permission (every screen needs its defaults); owner-only values never appear.',
    },
    update: {
        method: 'PUT',
        path: '/api/settings/:key',
        pathParams: keyParams,
        body: UpdateSettingRequestSchema,
        responses: {
            200: SettingRowSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Change a company value. org/role: Owner or admin.edit (BR-ADM-01-04). owner-scope: the flag alone — 404 for anybody else, because the row must not leak. Audited with before/after (BR-ADM-01-09).',
    },
    lock: {
        method: 'PUT',
        path: '/api/settings/:key/lock',
        pathParams: keyParams,
        body: LockSettingRequestSchema,
        responses: {
            200: SettingRowSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The padlock (§15.6.5): open an org/role setting for personal overrides, or close it. Audited.',
    },
    override: {
        method: 'PUT',
        path: '/api/settings/:key/my',
        pathParams: keyParams,
        body: UpdateSettingRequestSchema,
        responses: {
            200: SettingRowSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The caller’s OWN value: personal settings always (BR-ADM-01-03, no permission); org/role only while unlocked (BR-ADM-01-05). Never audited (BR-ADM-01-09).',
    },
    clearOverride: {
        method: 'DELETE',
        path: '/api/settings/:key/my',
        pathParams: keyParams,
        responses: {
            200: SettingRowSchema,
            401: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Drop the caller’s override, returning them to the company value.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=org-settings.js.map